/**
 * Customer-story videos for the /our-customers hero carousel.
 *
 * Copy (tag / title / description) lives in the localized message files under
 * `pages.customers.hero.episodes.<slug>`. Playback IDs and per-locale ordering
 * live here.
 */
export const CUSTOMER_VIDEOS = {
  "3bears": "QZ8XL86X5utg02REqjUyiz16duW9igY02SOVfaMfkMGM00",
  "petshop-co-uk": "zvjyY01muqPpnT013018p6jtOUHL8Qwb1PihVc9ovseXZs",
  "kekoa-foods": "9dpBqzFuO5LzhkBWAcwyWCyag1oaopCCqCO5h6h8JV8",
  "ghost-golf": "Y004LOYCOAa7ZhB8YHX2o18fPKsxvVhOtZBpoKa02sDBQ",
  "sand-cloud": "YeqAo02RI2VYmHSOHkdyeRC4trbhienre00nCasGvLPkA",
  "true-classic": "EC00dwAxMzhYtIeZEjYgAhNIz02MRt2mM2rzTTHLbiPL8",
  correxiko: "gzJo402jlVrKPax77J2aSnV5hDdeo92ttdyfchJ01dZ7I",
  "little-words-project": "x9RP47vs6uHqrDo24WnZ4IKTSQAcXTXC02uSrbK9eo028",
  spongelle: "0102TD5yIkednGJRopnO00obj51XQoIj02cUV8Dc02OCoB00M",
} as const;

export type CustomerVideoSlug = keyof typeof CUSTOMER_VIDEOS;

/**
 * Fallback order: used for any locale without its own lead list, and to fill
 * the tail after a locale's pinned leads.
 */
const DEFAULT_ORDER: CustomerVideoSlug[] = [
  "sand-cloud",
  "true-classic",
  "little-words-project",
  "ghost-golf",
  "3bears",
  "petshop-co-uk",
  "correxiko",
  "kekoa-foods",
  "spongelle",
];

/** Per-locale pinned leads; remaining videos follow in DEFAULT_ORDER. */
const LOCALE_LEADS: Record<string, CustomerVideoSlug[]> = {
  en: ["sand-cloud", "true-classic", "little-words-project", "ghost-golf"],
  es: ["sand-cloud", "true-classic", "little-words-project", "ghost-golf"],
  "en-GB": ["petshop-co-uk", "correxiko"],
  "en-IE": ["petshop-co-uk", "correxiko"],
  de: ["3bears"],
  nl: ["3bears"],
};

export function customerVideosForLocale(
  locale: string
): { slug: CustomerVideoSlug; playbackId: string }[] {
  const leads = LOCALE_LEADS[locale] ?? [];
  const ordered = [
    ...leads,
    ...DEFAULT_ORDER.filter((slug) => !leads.includes(slug)),
  ];
  return ordered.map((slug) => ({ slug, playbackId: CUSTOMER_VIDEOS[slug] }));
}

export function muxThumbnail(playbackId: string) {
  return `https://image.mux.com/${playbackId}/thumbnail.webp?fit_mode=smartcrop&time=0`;
}

/** Centred carousel preview length (5–10 seconds). */
export const CAROUSEL_PREVIEW_DURATION_SEC = 7;

/** Animated preview of the first N seconds — loops over image.mux.com. */
export function muxAnimatedPreview(
  playbackId: string,
  {
    start = 0,
    end = CAROUSEL_PREVIEW_DURATION_SEC,
    width = 640,
    fps = 15,
  }: { start?: number; end?: number; width?: number; fps?: number } = {}
) {
  const params = new URLSearchParams({
    start: String(start),
    end: String(end),
    width: String(width),
    fps: String(fps),
  });
  return `https://image.mux.com/${playbackId}/animated.webp?${params}`;
}
